import App from "../App";
import debugModel from "../../src/model/DebugModel";
import GameModel from "../model/GameModel";
import * as debug_config from "../../../../game/configs/debug-config.json";
import FullscreenUtils from "./FullscreenUtils";
import Utils from "./Utils";
import GameEvents from "../const/GameEvents";


enum CheatType {
    CustomInput,
    ReelStops,
    Feature,
    Seed,
    Extended,
    FakeResponse,
    FakeJackpot
}

export default class DebugMenu {
    protected container: HTMLElement;
    public menu: HTMLDivElement;
    protected menuBtn: HTMLDivElement;
    protected fps: HTMLDivElement;
    protected icon: HTMLElement;
    protected content: HTMLDivElement;
    protected isOpened: boolean;
    protected app: App;
    private gameConfig: any;
    private cheatTypes = [
        { type: CheatType.CustomInput, icon: "", order: "0" },
        { type: CheatType.ReelStops, icon: "▦", order: "1" },
        { type: CheatType.Seed, icon: "🌱", order: "2" },
        { type: CheatType.Feature, icon: "Ⓕ", order: "3" },
        { type: CheatType.Extended, icon: "☀", order: "4" },
        { type: CheatType.FakeResponse, icon: "⚠", order: "5" },
        { type: CheatType.FakeJackpot, icon: "🎰", order: "6" }
    ];

    constructor(container: HTMLElement) {
        if (GameModel.urlParams.removeDebug == "true") {
            return;
        }
        this.menu = document.createElement("div");
        this.menuBtn = document.createElement("div");
        this.icon = document.createElement("i");
        this.icon.classList.add("wiener_icon");
        this.content = document.createElement("div");
        this.menu.appendChild(this.menuBtn);
        this.menu.appendChild(this.content);
        container.appendChild(this.menu);
        this.menuBtn.appendChild(this.icon);
        this.menu.classList.add("wiener_debugMenu");
        this.menu.style.top = "0px";
        this.menuBtn.classList.add("wiener_debugBtn");
        this.content.classList.add("wiener_debuContent");
        this.closeMenu();

        this.menuBtn.addEventListener("click", this.toggleMenu.bind(this));
        // if (App.config.showFPS) {
        this.fps = document.createElement("div");
        this.fps.classList.add("wiener_debugMenu_fps");
        this.fps.style.top = this.menu.style.top;
        this.menuBtn.appendChild(this.fps);
        App.dispatcher.on(GameEvents.DRAW_CALLS_COMPUTED, (drawCalls: number) => {
            const currentFps = Math.round(App.instance.ticker.FPS);
            const averageFps = Math.round(App.instance.averageFPS);
            const dc = Math.round(drawCalls);
            const frameTime = App.instance.frameTime;
            this.fps.innerHTML = ("00" + currentFps).slice(-3) + "@" + ("00" + averageFps).slice(-3) + "<br/>&nbsp;DC: " + ("00" + dc).slice(-3) + "</br>&nbsp&nbsp";
            const isGameHeavy = dc > 50 || averageFps < 30 || frameTime > (1 / 60) * 1000;
            this.fps.style.color =  isGameHeavy ? "red" : "";
            this.fps.style.fontWeight = isGameHeavy ? "bold" : "";
            this.fps.style.backgroundColor = isGameHeavy ? "#0000005f" : "";
            this.fps.innerHTML += `${("00" + frameTime).slice(-3)} ms`;
        }, this);
        // }
        this.gameConfig = GameModel.gameConfig;
        if (this.gameConfig.extendedDebugMenu) {
            App.dispatcher.once(GameEvents.REEL_SETS_INITIALIZED, () => {
                this.createExtendedDebugMenu();
                this.createFakeJackpotsDebugMenu();
            }, this);
        }
    }

    private createExtendedDebugMenu() {
        for (let symIndex in this.gameConfig.symbolAssetsMap) {
            for (let i = 3; i < 6; ++i) {
                const { name, data } = this.createExtendedCheat(i, +symIndex);
                if (name && data) {
                    this.addDebugCase(
                        name,
                        data,
                        false,
                        true
                    );
                }
                if (this.gameConfig.reelSets[0].length === 3) break;
            }
        }
    }

    private createFakeJackpotsDebugMenu() {
        if (App.config.hasJackpotsFeature != true) {
            return;
        }

        const jackpots = [
            { name: "Mini", amount: 50.00 },
            { name: "Minor", amount: 1000.00 },
            { name: "Major", amount: 49999.999 },
            { name: "Grand", amount: 100100100.100 }
        ];

        jackpots.forEach(jackpot => {
            this.addDebugCase(
                `Fake ${jackpot.name} Jackpot [${jackpot.amount}]`,
                { jackpot: jackpot.name, amount: jackpot.amount },
                false,
                false
            );
        })
    }

    private createExtendedCheat(symsAmount: number, symIndex: number) {
        const symName = this.gameConfig.symbolAssetsMap[symIndex].name;
        if (this.gameConfig.reelSets[0].length <= 1) {
            // Relax game
            if (!Utils.matrixInclues(this.gameConfig.defaultSymbols, symName)) {
                // Default symbols does not include this sym, so no cheat for it.
                return { name: '', data: [] };
            } else {
                return {
                    "name": `${symsAmount} syms ${symName}`,
                    "data": { "winSymbol": symName, "winSymbolCount": symsAmount  }
                };
            }
        } else {
            // ALT game
            const reelsStopPositions = [];
            for (let i = 0; i < symsAmount; ++i) {
                const stopPosition = (this.gameConfig.reelSets[0][i] as Array<any>)
                    .findIndex(temp => temp === symIndex);
                if (stopPosition === -1) return { name: '', data: [] };
                reelsStopPositions.push(stopPosition);
            };
            const cheat = {
                "name": `${symsAmount} syms ${symName}`,
                "data": { "reelStops": reelsStopPositions }
            };
    
            return cheat;
        }
    }


    /**
     * Add Input field to debug menu debug preset case with a callback
     * @param debugCaseName
     * @param data:any
     */
    addDebugCase(debugCaseName: string, data: any, special: boolean, isExtended: boolean = false) {
        const item = document.createElement("div");
        let cheat: { type: CheatType, icon: string, order: string };
        if (data.seed) {
            cheat = this.cheatTypes.find(x => x.type == CheatType.Seed);
        }
        
        if (data.feature) {
            cheat = this.cheatTypes.find(x => x.type == CheatType.Feature);
        }

        if (isExtended) {
            cheat = this.cheatTypes.find(x => x.type == CheatType.Extended);
        } else {
            if (data.reelStops) {
                cheat = this.cheatTypes.find(x => x.type == CheatType.ReelStops);
            }
        }

        if (data.jackpot) {
            cheat = this.cheatTypes.find(x => x.type == CheatType.FakeJackpot);
        }
        
        item.style.order = cheat ? cheat.order : "1";
        const prefix = cheat && cheat.icon.length ? cheat.icon + " " : "";
        item.innerHTML = "<p>" + prefix + debugCaseName + "</p>";
        item.classList.add("wiener_debug_item");
        this.content.appendChild(item);
        item.addEventListener("click", () => {
            // callback();
            debugModel.currentDebugCase = debugCaseName;
            debugModel.debugData = data;
            debugModel.special = special
            this.toggleMenu();
        });
    }

    addFakeResponseDebugCase(debugCaseName: string, response: object) {
        const item = document.createElement("div");
        const cheat = this.cheatTypes.find(x => x.type == CheatType.FakeResponse)
        item.style.order = cheat.order;
        const prefix = cheat.icon.length ? (cheat.icon + " ") : "";
        item.innerHTML = "<p>" + prefix + debugCaseName + "</p>";
        item.classList.add("wiener_debug_item");
        this.content.appendChild(item);
        item.addEventListener("click", () => {
            // callback();
            debugModel.currentDebugCase = debugCaseName;
            debugModel.fakeResponse = response;
            this.toggleMenu();
        });
    }

    /**
     * Add Input field to debug menue
     * @param inputName
     * @param callback:string->void
     */
    addDebugInput(debugCaseName: string) {
        const item = document.createElement("div");
        const cheat = this.cheatTypes.find(x => x.type == CheatType.CustomInput);
        item.style.order = cheat.order;
        const prefix = cheat.icon.length ? cheat.icon + " " : "";
        item.innerHTML = "<p>" + prefix + debugCaseName + "</p>";
        item.classList.add("wiener_debug_item");
        this.content.appendChild(item);
        item.addEventListener("click", () => {
            const input = prompt("Enter Debug string:");
            if (input) {
                // callback(input);
                debugModel.currentDebugCase = debugCaseName;
                debugModel.debugData = input;
                this.toggleMenu();
            }
        });
    }

    toggleMenu() {
        if (this.isOpened) {
            this.closeMenu();
        } else {
            this.openMenu();
        }
    }

    openMenu() {
        this.icon.classList.remove("bug");
        this.icon.classList.add("close");
        this.menu.classList.remove("wiener_debugMenu_closed");
        this.menu.classList.add("wiener_debugMenu_opened");
        this.isOpened = true;
    }

    closeMenu() {
        this.icon.classList.remove("close");
        this.icon.classList.add("bug");
        this.menu.classList.remove("wiener_debugMenu_opened");
        this.menu.classList.add("wiener_debugMenu_closed");
        this.isOpened = false;
    }
}
